import { computed } from "vue";

import useLegacyStore from "@Composables/useLegacyStore";

/**
 * @typedef {Object} UseCaptcha
 *
 * @property {String}       getSiteKey             The site key.
 * @property {Boolean}      isAntiRobotWhitelisted Indicates whether anti robot is whitelisted or not.
 * @property {() => string} getAntiRobotTestToken  Gets a random anti robot token for test purpose.
 */

/**
 * The captcha composable.
 *
 * @returns {UseCaptcha} The composable object handler.
 */
export default function useCaptcha() {
  const { getters } = useLegacyStore();

  /**
   * Get the anti robot configuration from Spark.
   *
   * @returns A reactive object of anti robot configuration from Vuex Store.
   */
  const antiRobot = computed(() => getters.antiRobot);

  /**
   * Gets the site key from the anti robot mechanism.
   *
   * @returns The site key if the object is properly defined, otherwise undefined.
   * @remarks This method never throws.
   */
  const getSiteKey = computed(() => {
    return antiRobot?.value?.site_key || null;
  });

  /**
   * Indicates whether the anti robot mechanism is whitelisted or not.
   *
   * @returns True if anti robot is whitelisted, otherwise, false.
   */
  const isAntiRobotWhitelisted = computed(() => {
    return antiRobot?.value?.whitelist ?? false;
  });

  /**
   * Gets an anti robot test token.
   *
   * @returns The test token.
   */
  function getAntiRobotTestToken() {
    return (Math.random() + 1).toString(36);
  }

  return {
    getSiteKey,
    isAntiRobotWhitelisted,
    getAntiRobotTestToken,
  };
}
